import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { catchError, Observable, Subject, takeUntil, throwError } from 'rxjs';
import { Fournisseurs, UserByLaboEnseigne, User } from '../interfaces/fournisseurs/fournisseurs.model';
import { ConfigService } from './core/config.service';
import { UserKeycloak } from '../interfaces/keycloak/userKeycloak';

@Injectable({
  providedIn: 'root'
})
export class FournisseurService implements OnDestroy {
  private readonly _destroying$ = new Subject<void>();

  constructor(private readonly configService: ConfigService, private readonly http: HttpClient) { }

  /**
   * Called when the component is destroyed. Unsubscribes from all subscriptions to prevent memory leaks.
   */
  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Retrieves a list of all Labo Enseigne objects associated with a specific enseigne value from the backend API.
   * @param enseigne The enseigne value to filter the list of Labo Enseigne objects.
   * @returns An Observable of the list of Fournisseur objects associated with the given enseigne value.
   */
  public fetchAllLaboEnseigne(): Observable<Fournisseurs[]> {
    return this.http.get<Fournisseurs[]>(`${this.configService.config.backendUri}/Fournisseur/ListLaboEnseigne`)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        })
      );
  }

  /**
   * Retrieves a list of all FournisseurB2C objects from the backend API.
   * @returns An Observable of the list of FournisseurB2C objects.
   */
  public fetchAllFournisseur(): Observable<UserKeycloak[]> {
    return this.http.get<UserKeycloak[]>(`${this.configService.config.backendUri}/Fournisseur/ListFournisseur`)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        })
      );
  }

  /**
 * Retrieves a list of all UserByLaboEnseigne objects from the backend API.
 * @returns An Observable of the list of UserByLaboEnseigne objects.
 */
  public fetchAllUserByLaboEnseigne(idLaboEnseigne: number): Observable<UserByLaboEnseigne[]> {
    return this.http.get<UserByLaboEnseigne[]>(`${this.configService.config.backendUri}/Admin/ListUserByLaboEnseigne?idLaboEnseigne=${idLaboEnseigne}`)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        })
      );
  }

  /**
   * Links a list of UserFournisseurDTO objects to user accounts by making a POST request to the backend API.
   * @param fournisseursUsers The list of UserFournisseurDTO objects to link to user accounts.
   * @returns An Observable of the updated list of UserFournisseurDTO objects.
   */
  public linkFournisseurToUser(fournisseursUsers: User[]): Observable<User[]> {
    return this.http.post<User[]>(`${this.configService.config.backendUri}/Admin/LinkFournisseurToUser`, fournisseursUsers)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        })
      );
  }

  /**
   * Deletes a list of User objects from user accounts by making a DELETE request to the backend API.
   * @param users The list of User objects to delete.
   * @returns An Observable of the deleted list of User objects.
   */
  public deleteUsers(users: User[]): Observable<User[]> {
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      body: users
    };

    return this.http.delete<User[]>(`${this.configService.config.backendUri}/Admin/DeleteUsers`, options)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        })
      );
  }
}

